import { Color } from "@repo/app-types/constants/colors";
import { Size } from "@repo/ui/helpers/helpers";
import { Pill } from "@repo/ui/Pills/pill";
import { DateTime } from "luxon";
import { useEffect, useId, useMemo, useRef, useState, type FC } from "react";
import {
  Area,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { PerformanceAchievementQuarter } from "../../api/entities/performancePlaybook.js";
import { AnimatedProgressBar } from "./animatedProgressBar.js";

interface PerfectCallTier {
  readonly threshold: number;
  readonly name: string;
  readonly emoji: string;
  readonly colorHex: string;
}

const perfectCallTiers: readonly PerfectCallTier[] = [
  {
    threshold: 10,
    name: "Dialed In",
    emoji: "🎧",
    colorHex: "#14b8a6",
  },
  {
    threshold: 50,
    name: "Sharpshooter",
    emoji: "🎯",
    colorHex: "#10b981",
  },
  {
    threshold: 100,
    name: "Centurion",
    emoji: "🛡️",
    colorHex: "#3b82f6",
  },
  {
    threshold: 250,
    name: "Elite",
    emoji: "🔥",
    colorHex: "#6366f1",
  },
  {
    threshold: 500,
    name: "Diamond",
    emoji: "💎",
    colorHex: "#8b5cf6",
  },
  {
    threshold: 1000,
    name: "Phenom",
    emoji: "⚡",
    colorHex: "#f59e0b",
  },
  {
    threshold: 2500,
    name: "Masterclass",
    emoji: "🧠",
    colorHex: "#f97316",
  },
  {
    threshold: 5000,
    name: "Legend",
    emoji: "👑",
    colorHex: "#ef4444",
  },
  {
    threshold: 10000,
    name: "Hall of Fame",
    emoji: "🏛️",
    colorHex: "#a855f7",
  },
];

const preTier: PerfectCallTier = {
  threshold: 0,
  name: "Warming Up",
  emoji: "🚀",
  colorHex: "#0f766e",
};

const goldHex = "#eab308";

interface QuarterlyCumulativePoint {
  readonly quarterLabel: string;
  readonly cumulative: number;
  readonly increment: number;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function getCurrentTier(perfectCalls: number): PerfectCallTier | undefined {
  return perfectCallTiers
    .filter((tier) => perfectCalls >= tier.threshold)
    .at(-1);
}

function buildQuarterlyCumulativeData(
  quarterHistory: readonly PerformanceAchievementQuarter[],
): readonly QuarterlyCumulativePoint[] {
  const sortedHistory = [...quarterHistory].sort(
    (left, right) =>
      DateTime.fromISO(left.start_date).toMillis() -
      DateTime.fromISO(right.start_date).toMillis(),
  );

  const quarterlyPoints: QuarterlyCumulativePoint[] = [];
  let cumulativeCalls = 0;

  for (const quarter of sortedHistory) {
    const quarterStart = DateTime.fromISO(quarter.start_date);
    if (!quarterStart.isValid) continue;

    const increment = Math.max(
      0,
      Math.round(quarter.achievements.nb_perfect_calls),
    );
    cumulativeCalls += increment;
    quarterlyPoints.push({
      quarterLabel: `Q${quarterStart.quarter} ${String(quarterStart.year).slice(
        -2,
      )}`,
      cumulative: cumulativeCalls,
      increment,
    });
  }

  return quarterlyPoints;
}

const ChartTooltipContent = ({ active, payload }: TooltipContentProps) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as QuarterlyCumulativePoint | undefined;
  if (!point) return null;

  return (
    <div className="rounded-sm border border-border-light bg-white px-2 py-1 text-xs shadow-xs">
      <div className="font-medium text-muted-foreground">
        {point.quarterLabel}
      </div>
      <div className="text-foreground">
        {point.cumulative.toLocaleString()} cumulative calls
      </div>
      <div className="text-muted-foreground">
        +{point.increment.toLocaleString()} this quarter
      </div>
    </div>
  );
};

interface AchievementsPerfectCallsProps {
  perfectCalls: number;
  quarterHistory: readonly PerformanceAchievementQuarter[];
}

export const AchievementsPerfectCalls: FC<AchievementsPerfectCallsProps> = ({
  perfectCalls,
  quarterHistory,
}) => {
  const [animatedPerfectCalls, setAnimatedPerfectCalls] =
    useState(perfectCalls);
  const previousPerfectCallsRef = useRef(perfectCalls);

  useEffect(() => {
    const animationDurationMs = 950;
    const startValue = previousPerfectCallsRef.current;
    previousPerfectCallsRef.current = perfectCalls;
    const startTime = performance.now();
    let animationFrameId = 0;

    const step = (now: number) => {
      const progress = clamp((now - startTime) / animationDurationMs, 0, 1);
      const easedProgress = 1 - (1 - progress) ** 3;
      setAnimatedPerfectCalls(
        Math.round(startValue + (perfectCalls - startValue) * easedProgress),
      );

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(step);
      }
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [perfectCalls]);

  const currentTier = getCurrentTier(perfectCalls);
  const nextTier = perfectCallTiers.find(
    (tier) => tier.threshold > perfectCalls,
  );
  const activeTier = currentTier ?? preTier;
  const lowerMilestoneBound = currentTier?.threshold ?? 0;
  const upperMilestoneBound = nextTier?.threshold ?? lowerMilestoneBound;
  const progressToNextTier =
    upperMilestoneBound > lowerMilestoneBound
      ? clamp(
          (perfectCalls - lowerMilestoneBound) /
            (upperMilestoneBound - lowerMilestoneBound),
          0,
          1,
        )
      : 1;
  const displayPosition = progressToNextTier * 100;
  const callsNeededForNextTier = nextTier
    ? Math.max(nextTier.threshold - perfectCalls, 0)
    : 0;
  const quarterlyCumulativeData = useMemo(
    () => buildQuarterlyCumulativeData(quarterHistory),
    [quarterHistory],
  );

  const chartAreaGradientId = useId();

  return (
    <div className="rounded-md bg-white px-5 pt-3 pb-5 shadow-sm ring-1 ring-black/5">
      {/* Header: count + tier */}
      <div className="mt-3 flex items-center justify-between gap-4">
        <span className="text-3xl font-semibold text-foreground">
          {animatedPerfectCalls.toLocaleString()} perfect calls
        </span>
        <Pill
          label={
            activeTier.threshold > 0
              ? `${activeTier.emoji} ${activeTier.name} – ${activeTier.threshold.toLocaleString()} perfect calls`
              : `${activeTier.emoji} ${activeTier.name}`
          }
          size={Size.MEDIUM}
          color={Color.Amber}
        />
      </div>

      {/* Gap callout */}
      {nextTier ? (
        <p className="mt-2 text-sm text-foreground">
          <span className="font-semibold">
            {callsNeededForNextTier.toLocaleString()}
          </span>{" "}
          more perfect calls to unlock the{" "}
          <span className="font-semibold">{nextTier.name}</span> tier.
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          Highest tier unlocked. Keep it up!
        </p>
      )}

      {/* Progress bar */}
      <div className="relative mt-4">
        <AnimatedProgressBar displayPosition={displayPosition}>
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-400" />
        </AnimatedProgressBar>

        {/* Range labels below bar */}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] font-semibold text-muted-foreground">
            {lowerMilestoneBound.toLocaleString()}
          </span>
          <span className="text-[11px] font-semibold text-muted-foreground">
            {upperMilestoneBound.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-foreground">
            Quarterly trend
          </div>
        </div>
        <div className="h-28">
          {quarterlyCumulativeData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No perfect call history yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={[...quarterlyCumulativeData]}
                margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
              >
                <defs>
                  <linearGradient
                    id={chartAreaGradientId}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor={goldHex} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={goldHex} stopOpacity={0} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="quarterLabel"
                  tickMargin={6}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 11 }}
                />
                <YAxis
                  domain={["dataMin", "auto"]}
                  tickMargin={4}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9CA3AF", fontSize: 11 }}
                  tickFormatter={(value: number) => value.toLocaleString()}
                />
                <Tooltip
                  cursor={{
                    stroke: "#9CA3AF",
                    strokeDasharray: "3 3",
                    strokeWidth: 1,
                  }}
                  content={ChartTooltipContent}
                />

                <Area
                  dataKey="cumulative"
                  type="monotone"
                  stroke="none"
                  fill={`url(#${chartAreaGradientId})`}
                />
                <Line
                  dataKey="cumulative"
                  type="monotone"
                  stroke={goldHex}
                  strokeWidth={1.5}
                  dot={false}
                  activeDot={{
                    r: 3,
                    fill: goldHex,
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};
